import BulkTemplateReview from "./BulkTemplateReview";
import EditExistingTemplateFlow from "./EditExistingTemplateFlow";
import TemplateRequestHistory from "./TemplateRequestHistory";
import DocumentVariableMapper from "./documentMapper";
import ExistingTemplatePicker from "./documentMapper/ExistingTemplatePicker";
import { usePortalUrlState } from "./usePortalUrlState";
import {
  AddRounded as NewTemplateIcon,
  EditRounded as EditTemplateIcon,
  HistoryRounded as HistoryIcon,
  ArticleRounded as TemplatesIcon,
  ArrowBackRounded as BackIcon,
  NoteAddRounded as AddDocIcon,
  AutoFixHighRounded as ModifyIcon,
  DynamicFeedRounded as BulkIcon,
  ListAltRounded as PickExistingIcon,
  UploadFileRounded as UploadIcon,
} from "@mui/icons-material";
import {
  Badge,
  Box,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import * as logger from "common/logger";
import Button from "components/common/Button";
import TemplateValidationDialog from "components/common/TemplateValidationDialog";
import * as fileUtils from "fileUtils";
import { TEMPLATE_REQUESTS_EDIT_FLOW_V2 } from "flags";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  useConvertFileToPdfMutation,
  useCreateTemplateRequestMutation,
  useListTemplateRequestsQuery,
  useUpdateTemplateRequestMutation,
} from "services/docGenService";
import { useSendFeedbackMutation } from "services/submissionsService";
import { useIsVisible } from "store/hooks/staticDataHooks";
import * as userSelectors from "store/selectors/user/userSelectors";
import { v4 as uuidv4 } from "uuid";

const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div
      role={"tabpanel"}
      hidden={value !== index}
      id={`template-request-tabpanel-${index}`}
      aria-labelledby={`template-request-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
};

// Landing page with two options: Add New Template / Edit Existing Template
const RequestTypeLanding = ({ onSelectType }) => {
  const theme = useTheme();

  const options = [
    {
      type: "NEW_TEMPLATE",
      icon: AddDocIcon,
      title: "Add New Template",
      description:
        "Request a brand new document template. Upload your document and specify where data fields should be placed.",
      color: theme.palette.primary.main,
      features: [
        "Upload a Word document (.docx)",
        "Map data fields to specific locations",
        "Create custom fields if needed",
        "Track your request status",
      ],
    },
    {
      type: "EDIT_TEMPLATE",
      icon: ModifyIcon,
      title: "Edit Existing Template",
      description:
        "Request changes to an existing template. Pick one from this instance or upload a copy, then show us what needs to change.",
      color: theme.palette.secondary.main,
      features: [
        "Pick a template already in the instance",
        "Or upload a copy if you have one",
        "Add or relocate data fields",
        "Describe the changes needed",
      ],
    },
    {
      type: "BULK_REVIEW",
      icon: BulkIcon,
      title: "Bulk Template Review",
      description:
        "Upload multiple templates at once and review them sequentially. Mark each as needing changes or no changes required.",
      color: "#6366f1",
      features: [
        "Upload multiple templates at once",
        "Step through each template sequentially",
        "Add comments where changes are needed",
        "Mark templates as no changes required",
      ],
    },
  ];

  return (
    <Box marginBottom={4}>
      <Box sx={{ ml: { xs: 0, md: 4 }, mb: 4 }}>
        <Typography variant={"h6"} fontWeight={600}>
          {"What would you like to do?"}
        </Typography>
        <Typography variant={"body2"} color={"text.secondary"}>
          {"Select an option below to get started with your template request"}
        </Typography>
      </Box>

      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={4}
        justifyContent={"center"}
        alignItems={"stretch"}
      >
        {options.map((option) => {
          const Icon = option.icon;
          return (
            <Card
              key={option.type}
              elevation={0}
              sx={{
                flex: 1,
                maxWidth: 400,
                border: `1px solid ${theme.palette.divider}`,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: option.color,
                  boxShadow: `0 4px 20px ${option.color}20`,
                  transform: "translateY(-4px)",
                },
              }}
            >
              <CardActionArea
                onClick={() => onSelectType(option.type)}
                sx={{ height: "100%", p: 1 }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0.875rem",
                      borderRadius: "1rem",
                      backgroundColor: `${option.color}15`,
                      mb: 2,
                    }}
                  >
                    <Icon sx={{ fontSize: 36, color: option.color }} />
                  </Box>

                  <Typography
                    variant={"h6"}
                    sx={{ fontWeight: 600, mb: 1, color: option.color }}
                  >
                    {option.title}
                  </Typography>

                  <Typography
                    variant={"body2"}
                    color={"text.secondary"}
                    sx={{ mb: 2 }}
                  >
                    {option.description}
                  </Typography>

                  <Box
                    sx={{
                      textAlign: "left",
                      backgroundColor: theme.palette.grey[50],
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    {option.features.map((feature, idx) => (
                      <Stack
                        key={idx}
                        direction={"row"}
                        spacing={1}
                        alignItems={"center"}
                        sx={{ mb: idx < option.features.length - 1 ? 1 : 0 }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: option.color,
                          }}
                        />
                        <Typography
                          variant={"caption"}
                          color={"text.secondary"}
                        >
                          {feature}
                        </Typography>
                      </Stack>
                    ))}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Stack>
    </Box>
  );
};

// Reset state for the wizard. `source`, `initialFile`, and `templateId` only
// apply to the EDIT_TEMPLATE flow; null in every other case.
const INITIAL_WIZARD_STATE = {
  isActive: false,
  requestType: null,
  source: null,
  initialFile: null,
  templateId: null,
  initialPlacements: null,
  initialAnnotations: null,
  initialCustomFields: null,
  initialMetadata: null,
  // Within the v2 EDIT_TEMPLATE flow, which surface to show after the
  // user has picked a template: "form" (download / upload / describe) or
  // "mapper" (canvas with annotations + field placements). Defaults to
  // "form" on pick; the form's "Annotate this template" affordance flips
  // it to "mapper".
  editFlowSurface: "form",
  // The picked template's file_id, kept around so the mapper's Download
  // Original button can still return the system template even after the
  // user has uploaded their edited version (which becomes initialFile).
  originalS3FileId: null,
};

// Source-choice step shown for EDIT_TEMPLATE before the mapper:
// pick an existing template from the instance or upload a new file.
const EditTemplateSourceStep = ({ onPickExisting, onChooseUpload }) => {
  const theme = useTheme();
  const [mode, setMode] = useState(null);

  const cards = [
    {
      key: "existing",
      icon: PickExistingIcon,
      title: "Choose from existing templates",
      description:
        "Search this instance and load a template straight into the editor.",
      color: theme.palette.secondary.main,
      onClick: () => setMode("existing"),
    },
    {
      key: "upload",
      icon: UploadIcon,
      title: "Upload a file",
      description:
        "Drag in a copy of the template if you have one. Use this if your template isn't in the instance yet.",
      color: theme.palette.primary.main,
      onClick: onChooseUpload,
    },
  ];

  return (
    <Stack gap={3}>
      <Box>
        <Typography variant={"h6"} fontWeight={600}>
          {"Which template would you like to edit?"}
        </Typography>
        <Typography variant={"body2"} color={"text.secondary"}>
          {"Pick one already in the instance or upload a copy."}
        </Typography>
      </Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        alignItems={"stretch"}
      >
        {cards.map((card) => {
          const Icon = card.icon;
          const selected = mode === card.key;
          return (
            <Card
              key={card.key}
              elevation={0}
              sx={{
                flex: 1,
                border: `1px solid ${
                  selected ? card.color : theme.palette.divider
                }`,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: card.color,
                  boxShadow: `0 4px 20px ${card.color}20`,
                },
              }}
            >
              <CardActionArea
                onClick={card.onClick}
                sx={{ height: "100%", p: 1 }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0.625rem",
                      borderRadius: "0.75rem",
                      backgroundColor: `${card.color}15`,
                      mb: 1.5,
                    }}
                  >
                    <Icon sx={{ fontSize: 28, color: card.color }} />
                  </Box>
                  <Typography
                    variant={"subtitle1"}
                    sx={{ fontWeight: 600, color: card.color, mb: 0.5 }}
                  >
                    {card.title}
                  </Typography>
                  <Typography variant={"body2"} color={"text.secondary"}>
                    {card.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          );
        })}
      </Stack>
      {mode === "existing" && (
        <Box>
          <ExistingTemplatePicker onSelect={onPickExisting} />
        </Box>
      )}
    </Stack>
  );
};

const TemplateRequestPortal = () => {
  const theme = useTheme();

  // Get user info from Redux store
  const userEmail = useSelector(userSelectors.selectUserEmail);
  const isSuperUser = useSelector(userSelectors.isSuperUser);
  const isAdminUser = useSelector(userSelectors.isAdminUser);

  // Determine if user has admin privileges for template requests
  const isAdmin = isSuperUser || isAdminUser;

  // API hooks
  const [createTemplateRequest] = useCreateTemplateRequestMutation();
  const [convertFileToPdf] = useConvertFileToPdfMutation();
  // Feature flag for the LibreOffice server-side preview. When on, docx
  // uploads in the mapper are converted to PDF by the doc-gen worker and
  // rendered through the existing PDF embed path instead of the client-
  // side docx-preview library. Affects both New Template and Edit
  // Template flows.
  const { isVisible: useServerSidePdfPreview } = useIsVisible(
    "templateRequests.libreofficePreview"
  );
  const [updateTemplateRequestApi] = useUpdateTemplateRequestMutation();
  const [sendFeedback] = useSendFeedbackMutation();
  const {
    data: requestsData,
    isLoading,
    refetch: refetchRequests,
  } = useListTemplateRequestsQuery({ includeAll: isAdmin });

  const submittedRequests = requestsData?.requests || [];

  // The tab and the open wizard are positions, so they live in the path.
  // `wizardState` stays local because `initialFile` holds a File and
  // blob-backed data that cannot travel in a link; only which wizard is
  // open travels, as the second path segment.
  const {
    tab: activeTab,
    setTab,
    wizard: openWizard,
    setWizard,
    setPosition,
  } = usePortalUrlState();
  const [wizardState, setWizardState] = useState(INITIAL_WIZARD_STATE);

  // A status change the server refused because the completed template failed
  // validation. The report is shown and the admin may complete anyway with a
  // reason, which re-issues the same update with `force`.
  const [pendingValidation, setPendingValidation] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");

  // Feature flag for the v2 Edit Existing Template flow (2026-06-01 design
  // doc). When on, EDIT_TEMPLATE goes straight to the picker, then opens
  // the mapper with the picked template pre-loaded plus a Download
  // Original toolbar button so the user can pull the system docx for
  const { isVisible: editFlowV2Flag } = useIsVisible(
    TEMPLATE_REQUESTS_EDIT_FLOW_V2
  );
  const useEditFlowV2 = editFlowV2Flag ?? false;

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  // Start the wizard with selected type
  const handleSelectRequestType = (type) => {
    setWizard(type);
    setWizardState({
      ...INITIAL_WIZARD_STATE,
      isActive: true,
      requestType: type,
    });
  };

  // Go back to landing from wizard
  const handleBackToLanding = useCallback(() => {
    setWizard(null);
    setWizardState(INITIAL_WIZARD_STATE);
  }, [setWizard]);

  // Step back one level: from mapper back to source choice for EDIT_TEMPLATE,
  // or all the way to landing otherwise.
  const handleWizardBack = () => {
    if (
      wizardState.requestType === "EDIT_TEMPLATE" &&
      wizardState.source !== null
    ) {
      setWizardState((s) => ({
        ...s,
        source: null,
        initialFile: null,
        templateId: null,
      }));
      return;
    }
    handleBackToLanding();
  };

  const handlePickExistingTemplate = (selection) => {
    setWizardState((s) => ({
      ...s,
      source: "existing",
      initialFile: { file: selection.file, s3FileId: selection.s3FileId },
      templateId: selection.templateId,
      initialMetadata: {
        title: selection.templateTitle ?? "",
        description: "",
        priority: "MEDIUM",
      },
      // Default v2 surface to the form. The form's "Annotate this
      // template" button flips this to "mapper" via handleSwitchToAnnotate.
      editFlowSurface: "form",
    }));
  };

  // Called by the v2 form when the user wants annotation/field-placement
  // tooling on top of their already-uploaded edited version. The mapper
  // opens against the EDITED file (so the canvas shows the user's edits)
  // while the original template's s3FileId rides along separately so the
  // mapper's Download Original button still returns the system template.
  const handleSwitchToAnnotate = (selection) => {
    setWizardState((s) => ({
      ...s,
      source: "existing",
      initialFile: { file: selection.file, s3FileId: selection.s3FileId },
      templateId: selection.templateId,
      // Keep the original-template fileId discoverable by the mapper.
      originalS3FileId: selection.originalS3FileId,
      initialMetadata: {
        title: selection.templateTitle ?? "",
        description: "",
        priority: "MEDIUM",
      },
      editFlowSurface: "mapper",
    }));
  };

  const handleChooseUploadSource = () => {
    setWizardState((s) => ({
      ...s,
      source: "upload",
      initialFile: null,
      templateId: null,
    }));
  };

  // Resume a previously-submitted EDIT_TEMPLATE request: download the file
  // attached to the request, then open the mapper with its prior placements,
  // annotations, custom fields, and form metadata pre-loaded.
  const handleContinueEdit = useCallback(
    async (request) => {
      if (!request?.s3FileId) {
        alert(
          "This request has no document attached, so there is nothing to continue editing."
        );
        return;
      }
      try {
        const url = await fileUtils.getDownloadUrl(request.s3FileId);
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to download document (${response.status})`);
        }
        const blob = await response.blob();
        const filename = request.templateName || "template.docx";
        const file = new File([blob], filename, {
          type: request.fileType || blob.type,
        });
        const requestType = request.type || "EDIT_TEMPLATE";
        // The path names the wizard first, so the reconcile effect never sees
        // an open wizard the path does not name and close it again. Continuing
        // an edit runs from the history tab, so this yields `/all/edit`.
        setWizard(requestType);
        setWizardState({
          isActive: true,
          requestType,
          source: "upload",
          initialFile: { file, s3FileId: request.s3FileId },
          templateId: request.templateId ?? null,
          initialPlacements: request.placements ?? [],
          initialAnnotations: request.annotations ?? [],
          initialCustomFields: request.customFields ?? [],
          initialMetadata: {
            title: request.title ?? "",
            description: request.description ?? "",
            priority: request.priority ?? "MEDIUM",
          },
        });
      } catch (error) {
        logger.error(`Failed to load request for editing: ${error}`);
        alert(
          "Could not load that request for editing. Please try again or contact support."
        );
      }
    },
    [setWizard]
  );

  // Handle status change from admin
  const handleStatusChange = useCallback(
    async (requestId, { status, descriptionMessage }) => {
      const result = await updateTemplateRequestApi({
        requestId,
        status,
        descriptionMessage,
      });
      if (result?.error?.type === "TEMPLATE_VALIDATION_FAILED") {
        setOverrideReason("");
        setPendingValidation({
          requestId,
          status,
          report: result.error.body?.validation ?? null,
          message: result.error.message,
        });
        return;
      }
      refetchRequests();
    },
    [updateTemplateRequestApi, refetchRequests]
  );

  const handleCloseValidation = useCallback(() => {
    setPendingValidation(null);
    setOverrideReason("");
  }, []);

  const handleCompleteAnyway = useCallback(async () => {
    if (!pendingValidation) {
      return;
    }
    const { requestId, status } = pendingValidation;
    await updateTemplateRequestApi({
      requestId,
      status,
      descriptionMessage: overrideReason,
      force: true,
    });
    handleCloseValidation();
    refetchRequests();
  }, [
    pendingValidation,
    overrideReason,
    updateTemplateRequestApi,
    handleCloseValidation,
    refetchRequests,
  ]);

  // Handle new request submission from DocumentVariableMapper
  const handleSubmitMappingRequest = useCallback(
    async (requestData) => {
      const title =
        requestData.title ||
        `${
          wizardState.requestType === "NEW_TEMPLATE"
            ? "New Template"
            : "Template Edit"
        }: ${requestData.fileName}`;

      const description =
        requestData.description ||
        `${
          wizardState.requestType === "NEW_TEMPLATE"
            ? "New template request"
            : "Template modification request"
        } with ${requestData.placements?.length || 0} field placements and ${
          requestData.annotations?.length || 0
        } annotations.`;

      // Create request via API
      try {
        const result = await createTemplateRequest({
          requestType: wizardState.requestType,
          templateName: requestData.fileName,
          formNumber: requestData.formNumber,
          title,
          description,
          priority: requestData.priority || "MEDIUM",
          selectedFields:
            requestData.placements?.map((p) => p.variable.path) || [],
          customFields: requestData.customFields || [],
          placements: requestData.placements || [],
          annotations: requestData.annotations || [],
          s3FileId: requestData.s3FileId,
          fileType: requestData.fileType,
          // v2 Edit flow owns the picked template internally and passes
          // templateId through on the payload. Legacy flow stashes it in
          // wizardState. Honor whichever path we came in via.
          templateId: requestData.templateId ?? wizardState.templateId,
        });

        if (result.error) {
          logger.error(`Failed to create template request: ${result.error}`);
          alert(
            "Failed to submit template request. Please try again or contact support."
          );
          return;
        }
      } catch (error) {
        logger.error(`Failed to create template request: ${error}`);
        alert(
          "Failed to submit template request. Please try again or contact support."
        );
        return;
      }

      sendFeedback({
        type: "NEW_FEATURE_REQUEST",
        message: `[Template Request] ${title}\n\nPriority: ${
          requestData.priority || "MEDIUM"
        }\nDocument: ${requestData.fileName}\nFields placed: ${
          requestData.placements?.length || 0
        }\nAnnotations: ${
          requestData.annotations?.length || 0
        }\n\n${description}\n\nPlease check the Template Requests section in the admin console for this company.`,
        activeUrl: window.location.href,
      });

      refetchRequests();

      // Closing the wizard and landing on the history tab is one move, so it
      // is one write to the path. It replaces, because Back must not return to
      // the wizard that has just been submitted.
      setPosition(1, null, { replace: true });
      setWizardState(INITIAL_WIZARD_STATE);
    },
    [
      wizardState.requestType,
      wizardState.templateId,
      createTemplateRequest,
      sendFeedback,
      refetchRequests,
      setPosition,
    ]
  );

  // Count active (non-completed) submitted requests for badge
  const activeRequestCount = submittedRequests.filter(
    (r) => !["COMPLETED", "CANCELLED"].includes(r.status)
  ).length;

  const batchId = useState(() => `BATCH-${uuidv4()}`)[0];

  const handleBulkSubmitRequest = useCallback(
    async (requestData) => {
      const title = requestData.title || `Bulk Review: ${requestData.fileName}`;
      const description =
        requestData.description ||
        (requestData.status === "NO_CHANGES_REQUIRED"
          ? "Reviewed - no changes required."
          : `Template modification request with ${
              requestData.placements?.length || 0
            } field placements and ${
              requestData.annotations?.length || 0
            } annotations.`);

      await createTemplateRequest({
        requestType: "BULK_REVIEW",
        templateName: requestData.fileName,
        title,
        description,
        priority: requestData.priority || "MEDIUM",
        status: requestData.status || "SUBMITTED",
        selectedFields:
          requestData.placements?.map((p) => p.variable?.path) || [],
        customFields: requestData.customFields || [],
        placements: requestData.placements || [],
        annotations: requestData.annotations || [],
        s3FileId: requestData.s3FileId,
        fileType: requestData.fileType,
        batchId,
      });
    },
    [createTemplateRequest, batchId]
  );

  const handleBulkComplete = useCallback(() => {
    refetchRequests();
    setPosition(1, null, { replace: true });
    setWizardState(INITIAL_WIZARD_STATE);
  }, [refetchRequests, setPosition]);

  // The path is the source of truth for which wizard is open, so a cold
  // load, a shared link, Back and Forward all arrive here. Nothing of a
  // wizard's progress is encoded, so a wizard the path names opens at its
  // entry step with every other field back at its INITIAL_WIZARD_STATE
  // value.
  useEffect(() => {
    if (openWizard && openWizard !== wizardState.requestType) {
      setWizardState({
        ...INITIAL_WIZARD_STATE,
        isActive: true,
        requestType: openWizard,
      });
      return;
    }
    if (!openWizard && wizardState.isActive) {
      setWizardState(INITIAL_WIZARD_STATE);
    }
  }, [openWizard, wizardState.isActive, wizardState.requestType]);

  const getWizardTitle = () => {
    if (wizardState.requestType === "NEW_TEMPLATE") return "Add New Template";
    if (wizardState.requestType === "BULK_REVIEW")
      return "Bulk Template Review";
    return "Edit Existing Template";
  };

  const getWizardIcon = () => {
    if (wizardState.requestType === "NEW_TEMPLATE") return <NewTemplateIcon />;
    if (wizardState.requestType === "BULK_REVIEW") return <BulkIcon />;
    return <EditTemplateIcon />;
  };

  return (
    <Stack
      sx={{
        minWidth: "min(60rem, 100%)",
        maxWidth: "72rem",
        margin: "1rem auto",
        padding: "0 1rem",
      }}
    >
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          padding: "0.75rem",
          marginBottom: "1rem",
          borderRadius: "12px",
        }}
      >
        <Stack direction={"row"} alignItems={"center"} gap={2}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0.675rem",
              borderRadius: "0.5rem",
              backgroundColor: theme.palette.primary.main,
              color: theme.brand.white,
            }}
          >
            <TemplatesIcon sx={{ fontSize: 32 }} />
          </Box>
          <Stack sx={{ flex: 1 }}>
            <Typography variant={"h5"} sx={{ fontWeight: 600 }}>
              {"Document Template Requests"}
            </Typography>
            <Typography variant={"body2"} color={"text.secondary"}>
              {isAdmin
                ? "Manage and process template requests from all users."
                : "Request new templates or modifications to existing documents."}
            </Typography>
          </Stack>
          {isAdmin && <Chip label={"Admin View"} />}
        </Stack>
      </Paper>

      {/* Main Content */}
      <Paper elevation={0} sx={{ borderRadius: "12px", overflow: "hidden" }}>
        {/* Show wizard header when active */}
        {wizardState.isActive ? (
          <Box
            sx={{
              p: 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.grey[50],
            }}
          >
            <Stack direction={"row"} alignItems={"center"} gap={2}>
              <Button
                size={"small"}
                color={"inherit"}
                startIcon={<BackIcon />}
                onClick={handleWizardBack}
              >
                {"Back"}
              </Button>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color:
                    wizardState.requestType === "NEW_TEMPLATE"
                      ? theme.palette.primary.main
                      : theme.palette.secondary.main,
                }}
              >
                {getWizardIcon()}
                <Typography variant={"subtitle1"} sx={{ fontWeight: 600 }}>
                  {getWizardTitle()}
                </Typography>
              </Box>
            </Stack>
          </Box>
        ) : (
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              aria-label={"template request tabs"}
              sx={{
                px: 2,
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  minHeight: 56,
                },
              }}
            >
              <Tab
                icon={<NewTemplateIcon />}
                iconPosition={"start"}
                label={"New Request"}
                id={"template-request-tab-0"}
                aria-controls={"template-request-tabpanel-0"}
              />
              <Tab
                icon={
                  <Badge
                    badgeContent={activeRequestCount}
                    color={"warning"}
                    invisible={activeRequestCount === 0}
                  >
                    <HistoryIcon />
                  </Badge>
                }
                iconPosition={"start"}
                label={isAdmin ? "All Requests" : "Request History"}
                id={"template-request-tab-1"}
                aria-controls={"template-request-tabpanel-1"}
              />
            </Tabs>
          </Box>
        )}

        <Box sx={{ p: 2 }}>
          {wizardState.isActive ? (
            wizardState.requestType === "BULK_REVIEW" ? (
              <BulkTemplateReview
                onSubmitRequest={handleBulkSubmitRequest}
                onComplete={handleBulkComplete}
                onCancel={handleBackToLanding}
                useServerSidePdfPreview={useServerSidePdfPreview}
                convertFileToPdfFn={convertFileToPdf}
              />
            ) : wizardState.requestType === "EDIT_TEMPLATE" &&
              useEditFlowV2 &&
              wizardState.editFlowSurface === "form" ? (
              // v2: the picker is the first step inside the form itself
              // (rendered before the user has picked a template), then the
              // form takes over post-pick. Users who want annotations
              // hit "Annotate this template" which flips to the mapper.
              <EditExistingTemplateFlow
                onSubmitRequest={handleSubmitMappingRequest}
                onCancel={handleBackToLanding}
                onAnnotateInstead={handleSwitchToAnnotate}
              />
            ) : wizardState.requestType === "EDIT_TEMPLATE" &&
              !useEditFlowV2 &&
              wizardState.source === null ? (
              <EditTemplateSourceStep
                onPickExisting={handlePickExistingTemplate}
                onChooseUpload={handleChooseUploadSource}
              />
            ) : (
              <DocumentVariableMapper
                requestType={wizardState.requestType}
                initialFile={wizardState.initialFile}
                initialPlacements={wizardState.initialPlacements}
                initialAnnotations={wizardState.initialAnnotations}
                initialCustomFields={wizardState.initialCustomFields}
                initialMetadata={wizardState.initialMetadata}
                onSubmitRequest={handleSubmitMappingRequest}
                onCancel={handleBackToLanding}
                useServerSidePdfPreview={useServerSidePdfPreview}
                convertFileToPdfFn={convertFileToPdf}
                originalS3FileId={
                  useEditFlowV2
                    ? wizardState.originalS3FileId ??
                      wizardState.initialFile?.s3FileId
                    : null
                }
                originalFileName={
                  useEditFlowV2
                    ? wizardState.initialMetadata?.title
                      ? `${wizardState.initialMetadata.title}.docx`
                      : null
                    : null
                }
              />
            )
          ) : (
            <>
              <TabPanel value={activeTab} index={0}>
                <RequestTypeLanding onSelectType={handleSelectRequestType} />
              </TabPanel>
              <TabPanel value={activeTab} index={1}>
                <TemplateRequestHistory
                  submittedRequests={submittedRequests}
                  currentUserEmail={userEmail}
                  isAdmin={isAdmin}
                  isLoading={isLoading}
                  onSaveRequest={handleStatusChange}
                  onContinueEdit={handleContinueEdit}
                />
              </TabPanel>
            </>
          )}
        </Box>
      </Paper>

      <TemplateValidationDialog
        open={pendingValidation != null}
        onClose={handleCloseValidation}
        reports={pendingValidation?.report ? [pendingValidation.report] : []}
        onProceed={handleCompleteAnyway}
        proceedLabel={"Complete anyway"}
        requireReason
        reason={overrideReason}
        onReasonChange={setOverrideReason}
        failureMessage={pendingValidation?.message ?? null}
      />
    </Stack>
  );
};

export default TemplateRequestPortal;
